import {
  Box, Container, Typography, Stack, Chip, Divider, Link as MuiLink, Paper,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  FaShieldAlt, FaUserShield, FaChartBar, FaShareAlt,
  FaLock, FaBalanceScale, FaChild, FaExternalLinkAlt,
  FaSyncAlt, FaEnvelope, FaPhone, FaArrowLeft,
} from "react-icons/fa";



// ─── Section data ─────────────────────────────────────────────────────────────
const sections = [
  {
    num: "01", icon: <FaUserShield size={18} />, color: "#0288D1",
    bg: "rgba(2,136,209,0.08)", border: "rgba(2,136,209,0.18)",
    title: "Information We Collect",
    content: [
      {
        sub: "Personal Information",
        text: "When you create an account or use certain features, we may collect:",
        list: ["Full name", "Email address", "Phone number (if provided)", "Location (optional, based on your settings)"],
      },
      {
        sub: "Usage Data",
        text: "We automatically collect information about your interaction with the app:",
        list: ["Device information (type, OS, app version)", "Browsing data within the app", "Access times and pages viewed"],
      },
      {
        sub: "Payment Information",
        text: null,
        highlight: "LiteUpper does not store your credit or debit card information. All payments are handled through a secure third-party payment processor.",
      },
    ],
  },
  {
    num: "02", icon: <FaChartBar size={18} />, color: "#7c3aed",
    bg: "rgba(124,58,237,0.08)", border: "rgba(124,58,237,0.18)",
    title: "How We Use Your Information",
    list: [
      "Provide personalised learning content tailored to you.",
      "Improve app functionality and overall user experience.",
      "Communicate updates, new features, or promotions.",
      "Analyse app performance and usage trends.",
      "Prevent fraudulent or unauthorised activity.",
    ],
  },
  {
    num: "03", icon: <FaShareAlt size={18} />, color: "#059669",
    bg: "rgba(5,150,105,0.08)", border: "rgba(5,150,105,0.18)",
    title: "Sharing Your Information",
    intro: "We do not sell your personal information. We may share it only in these cases:",
    cards: [
      { label: "Service Providers", text: "Trusted third-party vendors (payment processors, analytics) for app-related services." },
      { label: "Legal Compliance",  text: "When required by law or to protect our legal rights." },
      { label: "With Consent",      text: "With your explicit consent for specific, stated purposes." },
    ],
  },
  {
    num: "04", icon: <FaLock size={18} />, color: "#d97706",
    bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.18)",
    title: "Data Security",
    text: "We implement robust measures to secure your information:",
    list: [
      "End-to-end encryption of sensitive data during transmission.",
      "Secure, access-controlled storage solutions.",
      "Strict, role-based access to personal data.",
    ],
  },
  {
    num: "05", icon: <FaBalanceScale size={18} />, color: "#0288D1",
    bg: "rgba(2,136,209,0.08)", border: "rgba(2,136,209,0.18)",
    title: "Your Rights",
    text: "Depending on your location, you may have the right to:",
    list: [
      "Access, update, or delete your personal information.",
      "Restrict or object to certain data processing activities.",
      "Request a portable copy of your data.",
      "Withdraw consent for data processing where applicable.",
    ],
    note: "To exercise your rights, reach us at liteupper@gmail.com",
  },
  {
    num: "06", icon: <FaChild size={18} />, color: "#7c3aed",
    bg: "rgba(124,58,237,0.08)", border: "rgba(124,58,237,0.18)",
    title: "Children's Privacy",
    text: "LiteUpper is designed for students aged 13 and above. We do not knowingly collect information from children under 13. If you believe a child has provided us with personal data, please contact us immediately so we can remove it.",
  },
  {
    num: "07", icon: <FaExternalLinkAlt size={18} />, color: "#059669",
    bg: "rgba(5,150,105,0.08)", border: "rgba(5,150,105,0.18)",
    title: "Third-Party Links",
    text: "The app may include links to third-party websites or services. This Privacy Policy does not apply to those platforms. We are not responsible for their content or privacy practices and encourage you to review their policies independently.",
  },
  {
    num: "08", icon: <FaSyncAlt size={18} />, color: "#d97706",
    bg: "rgba(217,119,6,0.08)", border: "rgba(217,119,6,0.18)",
    title: "Changes to This Policy",
    text: "We may update this Privacy Policy periodically. The latest version will always be available in the app. We will notify users of any significant changes through the app or via email.",
  },
];

// ─── Reusable section card ────────────────────────────────────────────────────
function SectionCard({ section }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${section.border}`,
        background: section.bg,
        overflow: "hidden",
        transition: "box-shadow 0.25s",
        "&:hover": { boxShadow: `0 8px 32px ${section.border}` },
      }}
    >
      {/* Card header */}
      <Stack
        direction="row" alignItems="center" spacing={2}
        sx={{
          px: 3, py: 2.2,
          borderBottom: `1px solid ${section.border}`,
          background: `linear-gradient(135deg, ${section.bg}, rgba(255,255,255,0.4))`,
        }}
      >
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
          background: `linear-gradient(135deg, ${section.color}22, ${section.color}11)`,
          border: `1px solid ${section.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: section.color,
        }}>
          {section.icon}
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, color: "#0c1a2e", fontSize: "1rem", letterSpacing: "-0.2px" }}>
            {section.title}
          </Typography>
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: "1.4rem", color: section.border, letterSpacing: "-1px", lineHeight: 1 }}>
          {section.num}
        </Typography>
      </Stack>

      {/* Card body */}
      <Box sx={{ px: 3, py: 2.5 }}>

        {/* Sub-sections (section 1 style) */}
        {section.content && (
          <Stack spacing={2.5}>
            {section.content.map((sub, i) => (
              <Box key={i}>
                <Typography sx={{ fontWeight: 700, color: "#0c1a2e", fontSize: "0.88rem", mb: 0.8 }}>
                  {sub.sub}
                </Typography>
                {sub.text && (
                  <Typography sx={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.7, mb: sub.list ? 1 : 0 }}>
                    {sub.text}
                  </Typography>
                )}
                {sub.list && (
                  <Stack spacing={0.6} sx={{ pl: 0.5 }}>
                    {sub.list.map((item, j) => (
                      <Stack key={j} direction="row" spacing={1.5} alignItems="flex-start">
                        <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: section.color, mt: "7px", flexShrink: 0 }} />
                        <Typography sx={{ color: "#64748b", fontSize: "0.87rem", lineHeight: 1.65 }}>{item}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
                {sub.highlight && (
                  <Box sx={{
                    mt: 0.5, px: 2, py: 1.5, borderRadius: 2,
                    background: "rgba(2,136,209,0.06)",
                    border: "1px solid rgba(2,136,209,0.15)",
                  }}>
                    <Typography sx={{ color: "#0288D1", fontSize: "0.87rem", fontWeight: 600, lineHeight: 1.65 }}>
                      {sub.highlight}
                    </Typography>
                  </Box>
                )}
                {i < section.content.length - 1 && (
                  <Divider sx={{ mt: 2, borderColor: section.border }} />
                )}
              </Box>
            ))}
          </Stack>
        )}

        {/* Simple intro + list */}
        {section.intro && (
          <Typography sx={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.7, mb: 2 }}>
            {section.intro}
          </Typography>
        )}

        {/* Mini cards (section 3 style) */}
        {section.cards && (
          <Stack spacing={1.5}>
            {section.cards.map((card, i) => (
              <Box key={i} sx={{
                px: 2, py: 1.5, borderRadius: 2,
                background: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}>
                <Typography sx={{ fontWeight: 700, color: "#0c1a2e", fontSize: "0.83rem", mb: 0.3 }}>
                  {card.label}
                </Typography>
                <Typography sx={{ color: "#64748b", fontSize: "0.84rem", lineHeight: 1.6 }}>
                  {card.text}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}

        {/* Plain text */}
        {section.text && !section.list && (
          <Typography sx={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.75 }}>
            {section.text}
          </Typography>
        )}

        {/* Text + bullet list */}
        {section.text && section.list && (
          <>
            <Typography sx={{ color: "#64748b", fontSize: "0.88rem", lineHeight: 1.7, mb: 1.5 }}>
              {section.text}
            </Typography>
            <Stack spacing={0.7}>
              {section.list.map((item, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: section.color, mt: "7px", flexShrink: 0 }} />
                  <Typography sx={{ color: "#64748b", fontSize: "0.87rem", lineHeight: 1.65 }}>{item}</Typography>
                </Stack>
              ))}
            </Stack>
          </>
        )}

        {/* Bullet list only */}
        {!section.text && section.list && (
          <Stack spacing={0.7}>
            {section.list.map((item, i) => (
              <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: section.color, mt: "7px", flexShrink: 0 }} />
                <Typography sx={{ color: "#64748b", fontSize: "0.87rem", lineHeight: 1.65 }}>{item}</Typography>
              </Stack>
            ))}
          </Stack>
        )}

        {/* Note / CTA */}
        {section.note && (
          <Box sx={{
            mt: 2, px: 2, py: 1.4, borderRadius: 2,
            background: "rgba(2,136,209,0.06)",
            border: "1px solid rgba(2,136,209,0.15)",
            display: "flex", alignItems: "center", gap: 1,
          }}>
            <FaEnvelope size={13} color="#0288D1" style={{ flexShrink: 0 }} />
            <Typography sx={{ color: "#0288D1", fontSize: "0.83rem", fontWeight: 600 }}>
              {section.note}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}



const PrivacyPage = () => {
   return (
     <Box
       sx={{
         minHeight: "100vh",
         background:
           "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #e1f5fe 100%)",
         position: "relative",
         overflow: "hidden",
       }}
     >
       {/* Ambient orbs */}
       <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
         <Box
           sx={{
             position: "absolute",
             width: 500,
             height: 500,
             borderRadius: "50%",
             top: -120,
             left: -100,
             background: "rgba(2,136,209,0.07)",
             filter: "blur(90px)",
           }}
         />
         <Box
           sx={{
             position: "absolute",
             width: 400,
             height: 400,
             borderRadius: "50%",
             bottom: -80,
             right: -80,
             background: "rgba(124,58,237,0.06)",
             filter: "blur(80px)",
           }}
         />
       </Box>

       {/* Dot grid */}
       <Box
         sx={{
           position: "absolute",
           inset: 0,
           opacity: 0.35,
           pointerEvents: "none",
           backgroundImage:
             "radial-gradient(circle, rgba(2,136,209,0.2) 1px, transparent 1px)",
           backgroundSize: "28px 28px",
         }}
       />

       <Container
         maxWidth="md"
         sx={{ position: "relative", zIndex: 1, py: { xs: 5, md: 8 } }}
       >
         {/* ── Back link ──────────────────────────────────────────────── */}
         <Box sx={{ mb: 4 }}>
           <MuiLink
             component={Link}
             to="/"
             underline="none"
             sx={{
               display: "inline-flex",
               alignItems: "center",
               gap: 1,
               color: "#0288D1",
               fontWeight: 700,
               fontSize: "0.85rem",
               "&:hover": { color: "#0277bd" },
               transition: "color 0.2s",
             }}
           >
             <FaArrowLeft size={13} /> Back to Home
           </MuiLink>
         </Box>

         {/* ── Hero header card ───────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             mb: 5,
             borderRadius: 4,
             overflow: "hidden",
             border: "1px solid rgba(2,136,209,0.15)",
             boxShadow: "0 8px 40px rgba(2,136,209,0.10)",
           }}
         >
           {/* Gradient banner */}
           <Box
             sx={{
               px: { xs: 3, md: 5 },
               py: { xs: 4, md: 5 },
               background:
                 "linear-gradient(135deg, #0288D1 0%, #0ea5e9 55%, #7c3aed 100%)",
               position: "relative",
               overflow: "hidden",
             }}
           >
             <Box
               sx={{
                 position: "absolute",
                 inset: 0,
                 opacity: 0.05,
                 backgroundImage:
                   "radial-gradient(circle, #fff 1px, transparent 1px)",
                 backgroundSize: "28px 28px",
                 pointerEvents: "none",
               }}
             />
             <Box sx={{ position: "relative", zIndex: 1 }}>
               <Stack
                 direction="row"
                 alignItems="center"
                 spacing={2}
                 sx={{ mb: 2 }}
               >
                 <Box
                   sx={{
                     width: 48,
                     height: 48,
                     borderRadius: 2,
                     background: "rgba(255,255,255,0.15)",
                     border: "1px solid rgba(255,255,255,0.25)",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                   }}
                 >
                   <FaShieldAlt size={22} color="#fff" />
                 </Box>
                 <Box>
                   <Chip
                     label="Legal"
                     size="small"
                     sx={{
                       mb: 0.5,
                       fontWeight: 700,
                       fontSize: "0.68rem",
                       background: "rgba(255,255,255,0.15)",
                       color: "#fff",
                       border: "1px solid rgba(255,255,255,0.2)",
                       letterSpacing: "0.5px",
                     }}
                   />
                   <Typography
                     variant="h4"
                     sx={{
                       fontWeight: 900,
                       color: "#fff",
                       letterSpacing: "-0.5px",
                       lineHeight: 1.1,
                     }}
                   >
                     Privacy Policy
                   </Typography>
                 </Box>
               </Stack>
               <Typography
                 sx={{
                   color: "rgba(255,255,255,0.78)",
                   fontSize: "0.95rem",
                   lineHeight: 1.7,
                   maxWidth: 560,
                 }}
               >
                 We respect your privacy and are committed to protecting the
                 personal information you share with us. This policy outlines
                 how we collect, use, and protect your data.
               </Typography>
             </Box>
           </Box>

           {/* Meta strip */}
           <Box
             sx={{
               px: { xs: 3, md: 5 },
               py: 1.8,
               background: "rgba(255,255,255,0.85)",
               backdropFilter: "blur(12px)",
               borderTop: "1px solid rgba(2,136,209,0.12)",
             }}
           >
             <Stack
               direction={{ xs: "column", sm: "row" }}
               spacing={{ xs: 1, sm: 3 }}
               alignItems={{ sm: "center" }}
             >
               <Typography
                 variant="caption"
                 sx={{ color: "#64748b", fontWeight: 600 }}
               >
                 Last Updated:{" "}
                 <Box
                   component="span"
                   sx={{ color: "#0288D1", fontWeight: 700 }}
                 >
                   December 9, 2024
                 </Box>
               </Typography>
               <Box
                 sx={{
                   display: { xs: "none", sm: "block" },
                   width: 4,
                   height: 4,
                   borderRadius: "50%",
                   background: "#cbd5e1",
                 }}
               />
               <Typography
                 variant="caption"
                 sx={{ color: "#64748b", fontWeight: 600 }}
               >
                 Applies to:{" "}
                 <Box
                   component="span"
                   sx={{ color: "#0288D1", fontWeight: 700 }}
                 >
                   LiteUpper App & Web Platform
                 </Box>
               </Typography>
             </Stack>
           </Box>
         </Paper>

         {/* ── Section cards ──────────────────────────────────────────── */}
         <Stack spacing={2.5} sx={{ mb: 4 }}>
           {sections.map((section) => (
             <SectionCard key={section.num} section={section} />
           ))}
         </Stack>

         {/* ── Contact card ───────────────────────────────────────────── */}
         <Paper
           elevation={0}
           sx={{
             borderRadius: 3,
             overflow: "hidden",
             border: "1px solid rgba(2,136,209,0.15)",
             boxShadow: "0 4px 24px rgba(2,136,209,0.08)",
           }}
         >
           <Stack
             direction="row"
             alignItems="center"
             spacing={2}
             sx={{
               px: 3,
               py: 2,
               borderBottom: "1px solid rgba(2,136,209,0.12)",
               background:
                 "linear-gradient(135deg, rgba(2,136,209,0.06), rgba(14,165,233,0.03))",
             }}
           >
             <Box
               sx={{
                 width: 36,
                 height: 36,
                 borderRadius: 1.5,
                 background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 boxShadow: "0 4px 10px rgba(2,136,209,0.3)",
               }}
             >
               <FaEnvelope size={15} color="#fff" />
             </Box>
             <Box>
               <Typography
                 sx={{ fontWeight: 800, color: "#0c1a2e", fontSize: "0.95rem" }}
               >
                 Contact Us
               </Typography>
               <Typography variant="caption" sx={{ color: "#64748b" }}>
                 Questions about this policy? We're here to help.
               </Typography>
             </Box>
           </Stack>

           <Box
             sx={{
               px: 3,
               py: 2.5,
               background: "rgba(255,255,255,0.75)",
               backdropFilter: "blur(12px)",
             }}
           >
             <Stack spacing={1.5}>
               <Stack direction="row" spacing={2} alignItems="center">
                 <Box
                   sx={{
                     width: 32,
                     height: 32,
                     borderRadius: 1.5,
                     background: "rgba(2,136,209,0.08)",
                     border: "1px solid rgba(2,136,209,0.18)",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     flexShrink: 0,
                   }}
                 >
                   <FaEnvelope size={13} color="#0288D1" />
                 </Box>
                 <Box>
                   <Typography
                     variant="caption"
                     sx={{
                       color: "#94a3b8",
                       fontWeight: 600,
                       display: "block",
                       fontSize: "0.7rem",
                       textTransform: "uppercase",
                       letterSpacing: "0.5px",
                     }}
                   >
                     Email
                   </Typography>
                   <MuiLink
                     href="mailto:liteupper@gmail.com"
                     underline="none"
                     sx={{
                       fontWeight: 700,
                       color: "#0288D1",
                       fontSize: "0.88rem",
                       "&:hover": { color: "#0277bd" },
                     }}
                   >
                     liteupper@gmail.com
                   </MuiLink>
                 </Box>
               </Stack>

               <Divider sx={{ borderColor: "rgba(2,136,209,0.1)" }} />

               <Stack direction="row" spacing={2} alignItems="center">
                 <Box
                   sx={{
                     width: 32,
                     height: 32,
                     borderRadius: 1.5,
                     background: "rgba(5,150,105,0.08)",
                     border: "1px solid rgba(5,150,105,0.18)",
                     display: "flex",
                     alignItems: "center",
                     justifyContent: "center",
                     flexShrink: 0,
                   }}
                 >
                   <FaPhone size={13} color="#059669" />
                 </Box>
                 <Box>
                   <Typography
                     variant="caption"
                     sx={{
                       color: "#94a3b8",
                       fontWeight: 600,
                       display: "block",
                       fontSize: "0.7rem",
                       textTransform: "uppercase",
                       letterSpacing: "0.5px",
                     }}
                   >
                     Phone
                   </Typography>
                   <Typography
                     sx={{
                       fontWeight: 700,
                       color: "#0c1a2e",
                       fontSize: "0.88rem",
                     }}
                   >
                     09061709128
                   </Typography>
                 </Box>
               </Stack>
             </Stack>
           </Box>
         </Paper>

         {/* ── Footer note ────────────────────────────────────────────── */}
         <Box sx={{ mt: 4, textAlign: "center" }}>
           <Typography
             variant="caption"
             sx={{ color: "#94a3b8", fontWeight: 500 }}
           >
             © {new Date().getFullYear()} LiteUpper · All rights reserved ·{" "}
             <MuiLink
               component={Link}
               to="/"
               underline="none"
               sx={{
                 color: "#0288D1",
                 fontWeight: 700,
                 "&:hover": { color: "#0277bd" },
               }}
             >
               Back to Home
             </MuiLink>
           </Typography>
         </Box>
       </Container>
     </Box>
   );
};

export default PrivacyPage;
